import { sessionToAuthTokens, type TokenStore } from './tokens';
import { ApiRequestError } from './errors';
import type { AuthTokens, SessionResponse } from './types';

export interface ApiClientConfig {
  baseUrl: string;
  tokenStore: TokenStore;
  onUnauthorized?: () => void;
}

export class ApiClient {
  private baseUrl: string;
  private tokenStore: TokenStore;
  private onUnauthorized?: () => void;
  private refreshPromise: Promise<AuthTokens> | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.tokenStore = config.tokenStore;
    this.onUnauthorized = config.onUnauthorized;
  }

  private async getAuthHeaders(isFormData = false): Promise<Record<string, string>> {
    const accessToken = await this.tokenStore.getAccessToken();
    const headers: Record<string, string> = {};
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return headers;
  }

  private async refreshAccessToken(): Promise<AuthTokens> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = await this.tokenStore.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = (async () => {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      // The refresh endpoint returns the full REQ-AUTH-001 session envelope,
      // not a bare AuthTokens — map it (fixes the expiry field) before storing.
      const session = (await response.json()) as SessionResponse;
      const tokens = sessionToAuthTokens(session);
      await this.tokenStore.setTokens(tokens);
      return tokens;
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const isFormData = init.body instanceof FormData;
    const headers = await this.getAuthHeaders(isFormData);

    let response = await fetch(url, {
      ...init,
      headers: { ...headers, ...init.headers },
    });

    if (response.status === 401) {
      try {
        await this.refreshAccessToken();
      } catch {
        await this.handleUnauthorized();
        throw new ApiRequestError({
          status: 401,
          message: 'Request failed with status 401',
          code: 'UNAUTHORIZED',
        });
      }
      const newHeaders = await this.getAuthHeaders(isFormData);
      response = await fetch(url, {
        ...init,
        headers: { ...newHeaders, ...init.headers },
      });
      if (response.status === 401) {
        await this.handleUnauthorized();
      }
    }

    if (!response.ok) {
      const body: Record<string, unknown> = await response.json().catch(() => ({}));
      const errorBody: Record<string, unknown> =
        (body['error'] as Record<string, unknown> | undefined) ?? body;
      const code =
        typeof errorBody['code'] === 'string' ? (errorBody['code'] as string) : undefined;
      const correlationId =
        typeof body['correlationId'] === 'string' ? (body['correlationId'] as string) : undefined;
      const message = `Request failed with status ${response.status}`;
      throw new ApiRequestError({
        status: response.status,
        message,
        code,
        correlationId,
      });
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  async get<T>(path: string): Promise<T> {
    return this.doRequest<T>(path, { method: 'GET' });
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    return this.doRequest<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.doRequest<T>(path, {
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.doRequest<T>(path, {
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.doRequest<T>(path, { method: 'DELETE' });
  }

  async setTokens(tokens: AuthTokens): Promise<void> {
    await this.tokenStore.setTokens(tokens);
  }

  async clearTokens(): Promise<void> {
    await this.tokenStore.clearTokens();
  }

  /**
   * Revokes the current refresh-token family and always clears local state.
   *
   * Logout is a coupled transaction: the bearer token authenticates the
   * request while the refresh token in its body selects the session family.
   * If the bearer expires at the request boundary, refresh rotates that body
   * token too, so retry the complete pair rather than replaying a stale body.
   */
  async revokeCurrentSession(): Promise<void> {
    try {
      if (!(await this.tokenStore.getAccessToken())) {
        await this.refreshAccessToken();
      }

      for (let attempt = 0; attempt < 2; attempt += 1) {
        const accessToken = await this.tokenStore.getAccessToken();
        const refreshToken = await this.tokenStore.getRefreshToken();
        if (!accessToken || !refreshToken) return;

        const response = await fetch(`${this.baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
        if (response.ok) return;
        if (response.status !== 401 || attempt === 1) return;

        // The bearer can expire between the snapshot and server verification.
        // Force rotation, then rebuild BOTH header and body on the next pass.
        await this.refreshAccessToken();
      }
    } catch {
      // Revocation is best-effort; the unconditional local clear is fail closed.
    } finally {
      await this.tokenStore.clearTokens();
    }
  }

  private async handleUnauthorized(): Promise<void> {
    await this.tokenStore.clearTokens();
    this.onUnauthorized?.();
  }

  /**
   * Returns a usable access token for out-of-band consumers that cannot ride
   * the request pipeline's 401→refresh retry — notably the voice WebSocket's
   * in-band auth frame (REQ-MOB-003 AC-06, parity with the web voice client).
   * Refreshes once if the stored access token is missing or expired; resolves
   * to null when no live session remains, so the caller fails closed to
   * sign-in rather than opening an unauthenticated channel.
   */
  async getValidAccessToken(): Promise<string | null> {
    const token = await this.tokenStore.getAccessToken();
    if (token) {
      return token;
    }
    try {
      const refreshed = await this.refreshAccessToken();
      return refreshed.accessToken;
    } catch {
      await this.handleUnauthorized();
      return null;
    }
  }
}
